import React, { useEffect, useState } from 'react';
import GeneratingLoader from '@/components/GeneratingLoader';
import ShimmerText from '@/components/ShimmerText';

interface UILoaderUnifiedProps {
  isDarkMode: boolean;
  context: 'image' | 'chat-agent' | 'chat-model' | 'web-search' | 'deep-research' | 'quiz';
  name?: string; // model or agent name
  userQuery?: string; // ✅ Added prop to accept actual query
}

export const UILoaderUnified: React.FC<UILoaderUnifiedProps> = ({ isDarkMode, context, name, userQuery: propUserQuery }) => {
  const [step, setStep] = useState<number>(0);
  const [userQuery, setUserQuery] = useState<string>('');

  // Get user query from props, URL params, or localStorage
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = propUserQuery || urlParams.get('q') || localStorage.getItem('currentQuery') || 'your query';
    setUserQuery(query);
  }, [propUserQuery]);

  // Helper function to truncate long queries
  const truncateQuery = (query: string, maxLength: number = 50) => {
    if (query.length <= maxLength) return query;
    return query.substring(0, maxLength) + '...';
  };

  const labels = (() => {
    if (context === 'image') return [''];

    const displayQuery = userQuery && userQuery !== 'your query' ? truncateQuery(userQuery) : null;

    if (context === 'web-search') {
      return [
        'Starting web search',
        displayQuery ? `Searching the web for "${displayQuery}"` : 'Searching the web',
        'Generating result'
      ];
    }

    // Deep research mode
    if (context === 'deep-research') {
      return [
        'Starting deep research',
        displayQuery ? `Analyzing web sources for "${displayQuery}"` : 'Analyzing web sources',
        'Synthesizing findings'
      ];
    }

    // Quiz mode
    if (context === 'quiz') {
      return [
        'Generating quiz',
        displayQuery ? `Creating questions on "${displayQuery}"` : 'Creating questions',
        'Finalizing quiz'
      ];
    }

    // Agent-specific loading messages
    if (context === 'chat-agent') {
      switch (name?.toLowerCase()) {
        case 'google search':
        case 'web search':
          return [
            'Starting Google Search',
            displayQuery ? `Searching for "${displayQuery}"` : 'Searching the web',
            'Processing results'
          ];
        case 'wikipedia':
          return [
            'Starting Wikipedia',
            displayQuery ? `Searching Wikipedia for "${displayQuery}"` : 'Searching Wikipedia',
            'Processing articles'
          ];
        case 'translator':
          return ['Starting translator', 'Analyzing text', 'Translating'];
        case 'weather':
          return ['Starting weather', 'Fetching weather data', 'Processing forecast'];
        case 'calculator':
          return ['Starting calculator', 'Processing calculation', 'Computing result'];
        case 'currency':
          return ['Starting converter', 'Fetching exchange rates', 'Converting currency'];
        case 'youtube':
          return [
            'Starting YouTube',
            displayQuery ? `Searching YouTube for "${displayQuery}"` : 'Searching YouTube',
            'Processing videos'
          ];
        case 'recipe':
          return [
            'Starting Recipe Search',
            displayQuery ? `Finding recipes for "${displayQuery}"` : 'Finding recipes',
            'Processing ingredients'
          ];
        case 'giphy':
          return [
            'Starting Giphy',
            displayQuery ? `Searching GIFs for "${displayQuery}"` : 'Searching GIFs',
            'Processing results'
          ];
        case 'meme':
          return ['Starting generator', 'Loading templates', 'Creating meme'];
        case 'qr-code':
          return ['Starting generator', 'Creating QR code', 'Processing image'];
        case 'password':
          return ['Starting generator', 'Creating secure password', 'Finalizing'];
        case 'color-palette':
          return ['Starting generator', 'Creating palette', 'Processing colors'];
        case 'pixabay':
          return [
            'Starting Pixabay',
            displayQuery ? `Searching images for "${displayQuery}"` : 'Searching images',
            'Processing results'
          ];
        case 'unit-converter':
          return ['Starting converter', 'Processing units', 'Converting values'];
        case 'lara':
          return [
            'LARA is analyzing your request',
            displayQuery ? `Planning tasks for "${displayQuery}"` : 'Planning tasks',
            'Executing agents'
          ];
        case 'code-explain':
        case 'code':
          return ['Starting analyzer', 'Analyzing code', 'Generating explanation'];
        default:
          return [
            `Starting ${name || 'agent'}`,
            displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
            'Generating response'
          ];
      }
    }

    // Model-specific loading messages
    if (context === 'chat-model') {
      const n = (name || 'AI').toLowerCase();
      switch (true) {
        case /chat\s*gpt|gpt|openai/.test(n):
          return [
            'Starting ChatGPT',
            displayQuery ? `Thinking about "${displayQuery}"` : 'Thinking',
            'Formulating answer'
          ];
        case /google\s*ai|googleai/.test(n):
          return [
            'Searching Google',
            displayQuery ? `Searching for "${displayQuery}"` : 'Searching',
            'Processing results'
          ];
        case /gemini|google/.test(n):
          return [
            'Starting Gemini',
            displayQuery ? `Analyzing "${displayQuery}"` : 'Analyzing',
            'Drafting response'
          ];
        case /deepseek/.test(n):
          return [
            'Starting DeepSeek',
            displayQuery ? `Reasoning on "${displayQuery}"` : 'Reasoning',
            'Composing answer'
          ];
        case /upstage|solar|pro/.test(n):
          return [
            'Starting Solar Pro',
            displayQuery ? `High-effort reasoning on "${displayQuery}"` : 'High-effort reasoning',
            'Producing response'
          ];
        case /sambanova|gpt-oss-120b|oss/.test(n):
          return [
            'Starting Sambanova AI',
            displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
            'Generating response'
          ];
        case /cerebras|qwen|235b/.test(n):
          return [
            'Starting Qwen-3',
            displayQuery ? `reasoning on "${displayQuery}"` : 'Fast reasoning',
            'Creating answer'
          ];
        case /qwen/.test(n):
          return [
            'Starting Qwen',
            displayQuery ? `Reasoning on "${displayQuery}"` : 'Reasoning',
            'Composing answer'
          ];
        case /glm/.test(n):
          return [
            'Starting GLM',
            displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
            'Generating response'
          ];
        case /gpt-oss|oss/.test(n):
          return [
            'Starting GPT-OSS',
            displayQuery ? `Processing "${displayQuery}` + '"' : 'Processing request',
            'Generating response'
          ];
        case /llama|meta/.test(n):
          return [
            'Starting Llama',
            displayQuery ? `Analyzing "${displayQuery}"` : 'Analyzing',
            'Writing response'
          ];
        case /groq|mixtral|mistral/.test(n):
          return [
            'Starting Groq',
            displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
            'Generating response'
          ];
        default:
          return [
            `Starting ${name || 'AI'}`,
            displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
            'Generating response'
          ];
      }
    }

    // Fallback
    return [
      `Starting ${name || 'AI'}`,
      displayQuery ? `Processing "${displayQuery}"` : 'Processing request',
      'Generating response'
    ];
  })();

  useEffect(() => {
    if (labels.length <= 1) return;
    setStep(0);

    // Show first message for longer, then cycle through others
    const timeouts: NodeJS.Timeout[] = [];

    // First message shows for 2 seconds
    timeouts.push(setTimeout(() => {
      setStep(1);

      // Then cycle through remaining messages every 2.5 seconds
      const id = setInterval(() => {
        setStep((s) => {
          const nextStep = s + 1;
          return nextStep >= labels.length ? 1 : nextStep; // Skip back to step 1, not 0
        });
      }, 2500);

      timeouts.push(id as any);
    }, 2000));

    return () => timeouts.forEach(clearTimeout);
  }, [context, name, labels.length, userQuery]); // Reset when any dependency changes

  if (context === 'image') {
    return (
      <div className="flex w-full items-center aspect-[16/9] justify-center">
        <div className="rounded-2xl w-full p-3 aspect-[16/9] bg-[#161616]">
          <GeneratingLoader isDarkMode={isDarkMode} />
        </div>
      </div>
    );
  }

  return (
    <div className="loader-wrapper">
      <div className="loader"></div>
      <div className="letter-wrapper">
        <ShimmerText text={labels[step]} isDarkMode={isDarkMode} />
      </div>
      <style>{`
        .loader-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          color: white;
          user-select: none;
          min-height: 24px;
        }
        .loader {
          width: 20px;
          height: 20px;
          aspect-ratio: 1 / 1;
          border-radius: 50%;
          background-color: transparent;
          animation: loader-rotate 1.5s linear infinite;
          z-index: 0;
          flex-shrink: 0;
        }
        @keyframes loader-rotate {
          0% {
            transform: rotate(90deg);
            box-shadow:
              0 1px 1px 0 #fff inset,
              0 3px 5px 0 #ff5f9f inset,
              0 4px 4px 0 #0693ff inset;
          }
          50% {
            transform: rotate(270deg);
            background: #7c0911;
            box-shadow:
              0 1px 1px 0 #fff inset,
              0 3px 5px 0 #d60a47 inset,
              0 4px 4px 0 #fbef19 inset;
          }
          100% {
            transform: rotate(450deg);
            box-shadow:
              0 1px 1px 0 #fff inset,
              0 3px 5px 0 #ff5f9f inset,
              0 4px 4px 0 #28a9ff inset;
          }
        }
        .letter-wrapper {
          display: flex;
          gap: 1px;
        }
      `}</style>
    </div>
  );
};

export default UILoaderUnified;


